import React, { memo, useCallback } from 'react';

import { Typography } from '@mui/material';
import { AutocompleteCDS, TextFieldCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import PropTypes from 'prop-types';

import { NO_CONTACT_PLACEHOLDER } from '../../AppraisalOrderContactsCard.constants';

import { inputDescriptionStyles } from './OrderContactSelect.styles';
import {
  checkIsOrderContactOptionDisabled,
  checkIsSameOrderContactOption,
  getInputPropsWithLeadingEndAdornment,
  getOrderContactOptionGroup,
} from './OrderContactSelect.utils';

const OrderContactSelect = ({
  name,
  options,
  value = null,
  ariaLabel = '',
  isDisabled = false,
  isClearable = true,
  isFieldChanged = false,
  dataTestId = '',
  dataTestIdInput = '',
  onSelect,
  onClear,
}) => {
  const valueDescription = value?.description;

  const onChange = useCallback(
    (e, option) => {
      if (option) {
        onSelect(option);

        return;
      }

      onClear();
    },
    [onSelect, onClear],
  );

  const renderOptionContent = useCallback(
    ({ option, optionText }) => (
      <>
        <Typography noWrap variant="regular16">
          {optionText}
        </Typography>
        <Typography noWrap color="text.secondary" variant="regular14">
          {option.description}
        </Typography>
      </>
    ),
    [],
  );

  const renderInput = useCallback(
    params => (
      <TextFieldCDS
        {...params}
        fullWidth
        noValidate
        InputProps={getInputPropsWithLeadingEndAdornment({
          InputProps: params.InputProps,
          leadingAdornment: valueDescription && (
            <Typography
              noWrap
              color="text.secondary"
              component="span"
              sx={inputDescriptionStyles}
              variant="regular14"
            >
              {valueDescription}
            </Typography>
          ),
        })}
        ariaLabel={ariaLabel}
        dataTestId={dataTestIdInput}
        isFieldChanged={isFieldChanged}
        name={name}
        placeholder={NO_CONTACT_PLACEHOLDER}
      />
    ),
    [ariaLabel, dataTestIdInput, isFieldChanged, name, valueDescription],
  );

  return (
    <AutocompleteCDS
      OptionContent={renderOptionContent}
      dataTestId={dataTestId}
      disableClearable={!isClearable}
      disabled={isDisabled}
      getOptionDisabled={checkIsOrderContactOptionDisabled}
      groupBy={getOrderContactOptionGroup}
      isOptionEqualToValue={checkIsSameOrderContactOption}
      name={name}
      optionClassName="flex justify-between w-full gap-12"
      optionName="label"
      options={options}
      renderInput={renderInput}
      value={value}
      withSortedOptions={false}
      onChange={onChange}
    />
  );
};

OrderContactSelect.propTypes = {
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      description: PropTypes.string,
      group: PropTypes.string,
      isDisabled: PropTypes.bool,
    }),
  ).isRequired,
  onSelect: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  value: PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    description: PropTypes.string,
  }),
  ariaLabel: PropTypes.string,
  isDisabled: PropTypes.bool,
  isClearable: PropTypes.bool,
  isFieldChanged: PropTypes.bool,
  dataTestId: PropTypes.string,
  dataTestIdInput: PropTypes.string,
};

export default memo(OrderContactSelect);
